import { create } from 'zustand';
import type { LLMInstance, OCRInstance, ImageEditInstance, ImageEditSettings, TitleOptimizationConfig, LowQualityFilterConfig, SizeChartConfig, OCRSettings, SkuConfig } from '@/types';
import {
    DEFAULT_LLM_INSTANCES,
    DEFAULT_OCR_INSTANCES,
    DEFAULT_IMAGE_EDIT_INSTANCES,
    DEFAULT_IMAGE_EDIT_SETTINGS,
    DEFAULT_TITLE_CONFIG,
    DEFAULT_LOW_QUALITY_CONFIG,
    DEFAULT_SIZE_CHART_CONFIG,
    DEFAULT_OCR_SETTINGS,
    DEFAULT_SKU_CONFIG,
    LEGACY_DEFAULT_SKU_SPEC_PROMPT,
    STORAGE_KEYS,
} from '@/lib/constants';
import { loadFromStorage, removeFromStorage } from '@/lib/storage';

export interface ConfigPreferences {
    llmInstances: LLMInstance[];
    ocrInstances: OCRInstance[];
    imageEditInstances: ImageEditInstance[];
    imageEditSettings: ImageEditSettings;
    ocrSettings: OCRSettings;
    titleConfig: TitleOptimizationConfig;
    lowQualityConfig: LowQualityFilterConfig;
    sizeChartConfig: SizeChartConfig;
    skuConfig: SkuConfig;
    hiddenFields: string[];
    fieldOrder: string[];
    stepConfirmEnabled: boolean;
    stepBatchSize: number;
}

let persistPreferences: ((preferences: ConfigPreferences) => void) | null = null;

interface ConfigState {
    // LLM 实例
    llmInstances: LLMInstance[];
    // OCR 实例
    ocrInstances: OCRInstance[];
    // AI 图片编辑实例
    imageEditInstances: ImageEditInstance[];
    // AI 图片编辑全局设置
    imageEditSettings: ImageEditSettings;
    // OCR 设置
    ocrSettings: OCRSettings;
    // 标题优化配置
    titleConfig: TitleOptimizationConfig;
    // 低质量过滤配置
    lowQualityConfig: LowQualityFilterConfig;
    // 尺码图识别配置
    sizeChartConfig: SizeChartConfig;
    // SKU 配置
    skuConfig: SkuConfig;
    // 列隐藏配置（空数组=全部可见，仅前端展示，不影响导出）
    hiddenFields: string[];
    // 列自定义顺序（空数组=默认顺序）
    fieldOrder: string[];
    // 步进确认开关
    stepConfirmEnabled: boolean;
    // 步进批数（每 N 个产品确认一次）
    stepBatchSize: number;

    // LLM 操作
    addLLMInstance: (instance: LLMInstance) => void;
    updateLLMInstance: (id: string, updates: Partial<LLMInstance>) => void;
    deleteLLMInstance: (id: string) => void;
    reorderLLMInstances: (instances: LLMInstance[]) => void;

    // OCR 操作
    addOCRInstance: (instance: OCRInstance) => void;
    updateOCRInstance: (id: string, updates: Partial<OCRInstance>) => void;
    deleteOCRInstance: (id: string) => void;
    reorderOCRInstances: (instances: OCRInstance[]) => void;
    incrementOCRUsage: (id: string) => void;
    updateOCRSettings: (settings: Partial<OCRSettings>) => void;

    // AI 图片编辑操作
    addImageEditInstance: (instance: ImageEditInstance) => void;
    updateImageEditInstance: (id: string, updates: Partial<ImageEditInstance>) => void;
    deleteImageEditInstance: (id: string) => void;
    reorderImageEditInstances: (instances: ImageEditInstance[]) => void;
    updateImageEditSettings: (settings: Partial<ImageEditSettings>) => void;

    // 配置更新
    updateTitleConfig: (updates: Partial<TitleOptimizationConfig>) => void;
    updateLowQualityConfig: (updates: Partial<LowQualityFilterConfig>) => void;
    updateSizeChartConfig: (updates: Partial<SizeChartConfig>) => void;
    updateSkuConfig: (updates: Partial<SkuConfig>) => void;
    // 列可见性操作
    toggleFieldVisibility: (fieldKey: string) => void;
    setAllFieldsVisible: () => void;
    // 列顺序操作
    setFieldOrder: (order: string[]) => void;
    resetFieldOrder: () => void;
    // 步进确认操作
    toggleStepConfirm: () => void;
    setStepBatchSize: (size: number) => void;
}

// 仅用于把升级前的 localStorage 配置迁移到服务器；服务器配置载入后会清理这些旧键。
// 合并缺失的默认实例（如新增的 qwen 默认实例），避免老用户看不到新默认项
function normalizeLLMInstances(raw: unknown): LLMInstance[] {
    const instances = (Array.isArray(raw) ? raw : []).filter((item): item is Record<string, unknown> => !!item && typeof item === 'object').map((item, index) => ({
        id: typeof item.id === 'string' && item.id ? item.id : `legacy-llm-${index}`,
        name: typeof item.name === 'string' ? item.name : '',
        apiUrl: typeof item.apiUrl === 'string' ? item.apiUrl : '',
        apiKey: typeof item.apiKey === 'string' ? item.apiKey : '',
        models: Array.isArray(item.models) ? item.models.filter((model): model is string => typeof model === 'string') : [],
        protocol: item.protocol === 'sub2api' ? 'sub2api' as const : 'openai-compatible' as const,
    }));
    if (instances.length === 0) return DEFAULT_LLM_INSTANCES;
    const storedIds = new Set(instances.map((instance) => instance.id));
    const missing = DEFAULT_LLM_INSTANCES.filter((d) => !storedIds.has(d.id));
    return missing.length > 0 ? [...instances, ...missing] : instances;
}

function loadLLMInstances(): LLMInstance[] {
    return normalizeLLMInstances(loadFromStorage<unknown>(STORAGE_KEYS.LLM_INSTANCES, []));
}

function loadOCRInstances(): OCRInstance[] {
    const instances = normalizeOCRInstances(loadFromStorage<unknown>(STORAGE_KEYS.OCR_INSTANCES, DEFAULT_OCR_INSTANCES));
    // 合并使用次数
    const usage = loadFromStorage<Record<string, number>>(STORAGE_KEYS.OCR_USAGE, {});
    return instances.map(inst => ({
        ...inst,
        usedCount: usage[inst.id] ?? inst.usedCount ?? 0,
    }));
}

function loadOCRSettings(): OCRSettings {
    return loadFromStorage(STORAGE_KEYS.OCR_SETTINGS, DEFAULT_OCR_SETTINGS);
}

function loadImageEditInstances(): ImageEditInstance[] {
    return normalizeImageEditInstances(loadFromStorage(STORAGE_KEYS.IMAGE_EDIT_INSTANCES, DEFAULT_IMAGE_EDIT_INSTANCES));
}

function normalizeImageEditInstances(instances: ImageEditInstance[]): ImageEditInstance[] {
    const normalized = instances.filter(instance => !!instance && typeof instance === 'object').map((instance, index) => ({
        ...instance,
        id: typeof instance.id === 'string' && instance.id ? instance.id : `legacy-image-${index}`,
        name: typeof instance.name === 'string' ? instance.name : '',
        apiUrl: typeof instance.apiUrl === 'string' ? instance.apiUrl : '',
        apiKey: typeof instance.apiKey === 'string' ? instance.apiKey : '',
        model: typeof instance.model === 'string' ? instance.model : '',
        apiPath: typeof instance.apiPath === 'string' ? instance.apiPath : '/api/relay/image-jobs/edits',
        defaultRatio: typeof instance.defaultRatio === 'string' ? instance.defaultRatio : '1:1',
        defaultQuality: typeof instance.defaultQuality === 'string' ? instance.defaultQuality : 'standard',
        defaultCount: Math.max(1, Number(instance.defaultCount) || 1),
        provider: instance.provider === 'nano-banana' ? 'nano-banana' as const : 'relay' as const,
    }));
    const ids = new Set(normalized.map(instance => instance.id));
    const merged = [...normalized, ...DEFAULT_IMAGE_EDIT_INSTANCES.filter(instance => !ids.has(instance.id))];
    if (merged.some(instance => instance.callEnabled !== undefined)) return merged;
    return merged.map((instance, index) => ({ ...instance, callEnabled: index === 0 }));
}

function normalizeOCRInstances(value: unknown): OCRInstance[] {
    const source = Array.isArray(value) ? value : [];
    const instances = source.filter((item): item is Record<string, unknown> => !!item && typeof item === 'object').map((item, index) => ({
        id: typeof item.id === 'string' && item.id ? item.id : `legacy-ocr-${index}`,
        name: typeof item.name === 'string' ? item.name : '',
        type: typeof item.type === 'string' ? item.type : 'PaddleOCR',
        apiUrl: typeof item.apiUrl === 'string' ? item.apiUrl : '',
        token: typeof item.token === 'string' ? item.token : '',
        model: typeof item.model === 'string' ? item.model : '',
        enabled: item.enabled === true,
        quota: Math.max(0, Number(item.quota) || 0),
        usedCount: Math.max(0, Number(item.usedCount) || 0),
    }));
    const ids = new Set(instances.map(instance => instance.id));
    return [...instances, ...DEFAULT_OCR_INSTANCES.filter(instance => !ids.has(instance.id))];
}

function loadImageEditSettings(): ImageEditSettings {
    const stored = loadFromStorage(
        STORAGE_KEYS.IMAGE_EDIT_SETTINGS,
        DEFAULT_IMAGE_EDIT_SETTINGS,
    ) as ImageEditSettings & Record<string, unknown>;
    const settings: ImageEditSettings = {
        jdcloudStorageEnabled: typeof stored.jdcloudStorageEnabled === 'boolean'
            ? stored.jdcloudStorageEnabled
            : DEFAULT_IMAGE_EDIT_SETTINGS.jdcloudStorageEnabled,
        browserCompressionEnabled: typeof stored.browserCompressionEnabled === 'boolean'
            ? stored.browserCompressionEnabled
            : DEFAULT_IMAGE_EDIT_SETTINGS.browserCompressionEnabled,
        compressionLevel: stored.compressionLevel === 'low' || stored.compressionLevel === 'high'
            ? stored.compressionLevel
            : 'medium',
        defaultRatio: typeof stored.defaultRatio === 'string' ? stored.defaultRatio : undefined,
        defaultQuality: typeof stored.defaultQuality === 'string' ? stored.defaultQuality : undefined,
        defaultCount: Number.isFinite(Number(stored.defaultCount)) ? Math.max(1, Math.min(50, Number(stored.defaultCount))) : undefined,
        presetPrompts: Array.isArray(stored.presetPrompts)
            ? stored.presetPrompts.filter((prompt): prompt is string => typeof prompt === 'string')
            : DEFAULT_IMAGE_EDIT_SETTINGS.presetPrompts,
    };

    return settings;
}

function loadTitleConfig(): TitleOptimizationConfig {
    return { ...DEFAULT_TITLE_CONFIG, ...loadFromStorage(STORAGE_KEYS.TITLE_CONFIG, DEFAULT_TITLE_CONFIG) };
}

function loadLowQualityConfig(): LowQualityFilterConfig {
    return loadFromStorage(STORAGE_KEYS.LOW_QUALITY_CONFIG, DEFAULT_LOW_QUALITY_CONFIG);
}

function loadSizeChartConfig(): SizeChartConfig {
    return loadFromStorage(STORAGE_KEYS.SIZE_CHART_CONFIG, DEFAULT_SIZE_CHART_CONFIG);
}

function loadSkuConfig(): SkuConfig {
    const stored = loadFromStorage<SkuConfig>(STORAGE_KEYS.SKU_CONFIG, DEFAULT_SKU_CONFIG);
    // 合并默认值，确保老用户缺少新增字段时也能补全
    const merged = { ...DEFAULT_SKU_CONFIG, ...stored };
    if (merged.specPrompt === LEGACY_DEFAULT_SKU_SPEC_PROMPT) merged.specPrompt = DEFAULT_SKU_CONFIG.specPrompt;
    return merged;
}

function loadVisibleFields(): string[] {
    // 改为 hiddenFields 语义：空数组=全部可见
    return loadFromStorage<string[]>(STORAGE_KEYS.VISIBLE_FIELDS, []);
}

function loadFieldOrder(): string[] {
    return loadFromStorage<string[]>(STORAGE_KEYS.FIELD_ORDER, []);
}

function normalizeImageEditSettings(settings: Partial<ImageEditSettings> | undefined): ImageEditSettings {
    return {
        jdcloudStorageEnabled: typeof settings?.jdcloudStorageEnabled === 'boolean'
            ? settings.jdcloudStorageEnabled
            : DEFAULT_IMAGE_EDIT_SETTINGS.jdcloudStorageEnabled,
        browserCompressionEnabled: typeof settings?.browserCompressionEnabled === 'boolean'
            ? settings.browserCompressionEnabled
            : DEFAULT_IMAGE_EDIT_SETTINGS.browserCompressionEnabled,
        compressionLevel: settings?.compressionLevel === 'low' || settings?.compressionLevel === 'high'
            ? settings.compressionLevel
            : 'medium',
        defaultRatio: typeof settings?.defaultRatio === 'string' ? settings.defaultRatio : undefined,
        defaultQuality: typeof settings?.defaultQuality === 'string' ? settings.defaultQuality : undefined,
        defaultCount: Number.isFinite(Number(settings?.defaultCount)) ? Math.max(1, Math.min(50, Number(settings?.defaultCount))) : undefined,
        presetPrompts: Array.isArray(settings?.presetPrompts)
            ? settings.presetPrompts.filter((prompt): prompt is string => typeof prompt === 'string')
            : DEFAULT_IMAGE_EDIT_SETTINGS.presetPrompts,
    };
}

function normalizePreferences(preferences: Partial<ConfigPreferences>): ConfigPreferences {
    return {
        llmInstances: normalizeLLMInstances(preferences.llmInstances),
        ocrInstances: normalizeOCRInstances(preferences.ocrInstances),
        imageEditInstances: normalizeImageEditInstances(Array.isArray(preferences.imageEditInstances) ? preferences.imageEditInstances : DEFAULT_IMAGE_EDIT_INSTANCES),
        imageEditSettings: normalizeImageEditSettings(preferences.imageEditSettings),
        ocrSettings: { ...DEFAULT_OCR_SETTINGS, ...(preferences.ocrSettings ?? {}) },
        titleConfig: { ...DEFAULT_TITLE_CONFIG, ...(preferences.titleConfig ?? {}) },
        lowQualityConfig: { ...DEFAULT_LOW_QUALITY_CONFIG, ...(preferences.lowQualityConfig ?? {}) },
        sizeChartConfig: { ...DEFAULT_SIZE_CHART_CONFIG, ...(preferences.sizeChartConfig ?? {}) },
        skuConfig: (() => {
            const config = { ...DEFAULT_SKU_CONFIG, ...(preferences.skuConfig ?? {}) };
            if (config.specPrompt === LEGACY_DEFAULT_SKU_SPEC_PROMPT) config.specPrompt = DEFAULT_SKU_CONFIG.specPrompt;
            return config;
        })(),
        hiddenFields: Array.isArray(preferences.hiddenFields) ? preferences.hiddenFields.filter(value => typeof value === 'string') : [],
        fieldOrder: Array.isArray(preferences.fieldOrder) ? preferences.fieldOrder.filter(value => typeof value === 'string') : [],
        stepConfirmEnabled: typeof preferences.stepConfirmEnabled === 'boolean' ? preferences.stepConfirmEnabled : false,
        stepBatchSize: Math.max(1, Math.round(Number(preferences.stepBatchSize) || 1)),
    };
}

export function getConfigPreferences(): ConfigPreferences {
    const state = useConfigStore.getState();
    return {
        llmInstances: state.llmInstances,
        ocrInstances: state.ocrInstances,
        imageEditInstances: state.imageEditInstances,
        imageEditSettings: state.imageEditSettings,
        ocrSettings: state.ocrSettings,
        titleConfig: state.titleConfig,
        lowQualityConfig: state.lowQualityConfig,
        sizeChartConfig: state.sizeChartConfig,
        skuConfig: state.skuConfig,
        hiddenFields: state.hiddenFields,
        fieldOrder: state.fieldOrder,
        stepConfirmEnabled: state.stepConfirmEnabled,
        stepBatchSize: state.stepBatchSize,
    };
}

export function hydrateConfigPreferences(preferences: Partial<ConfigPreferences>): void {
    useConfigStore.setState(normalizePreferences(preferences));
}

export function setConfigPersistenceHandler(handler: ((preferences: ConfigPreferences) => void) | null): void {
    persistPreferences = handler;
}

export function persistCurrentConfigPreferences(): void {
    persistPreferences?.(getConfigPreferences());
}

export function clearLegacyConfigStorage(): void {
    Object.values(STORAGE_KEYS).forEach(removeFromStorage);
}

function persistConfig(): void {
    persistCurrentConfigPreferences();
}

export const useConfigStore = create<ConfigState>((set, get) => ({
    llmInstances: loadLLMInstances(),
    ocrInstances: loadOCRInstances(),
    imageEditInstances: loadImageEditInstances(),
    imageEditSettings: loadImageEditSettings(),
    ocrSettings: loadOCRSettings(),
    titleConfig: loadTitleConfig(),
    lowQualityConfig: loadLowQualityConfig(),
    sizeChartConfig: loadSizeChartConfig(),
    skuConfig: loadSkuConfig(),
    hiddenFields: loadVisibleFields(),
    fieldOrder: loadFieldOrder(),
    stepConfirmEnabled: loadFromStorage(STORAGE_KEYS.STEP_CONFIRM_ENABLED, false),
    stepBatchSize: loadFromStorage(STORAGE_KEYS.STEP_BATCH_SIZE, 1),

    addLLMInstance: (instance: LLMInstance) => {
        const { llmInstances } = get();
        const newList = [...llmInstances, instance];
        set({ llmInstances: newList });
        persistConfig();
    },

    updateLLMInstance: (id: string, updates: Partial<LLMInstance>) => {
        const { llmInstances } = get();
        const newList = llmInstances.map(inst => inst.id === id ? { ...inst, ...updates } : inst);
        set({ llmInstances: newList });
        persistConfig();
    },

    deleteLLMInstance: (id: string) => {
        const { llmInstances } = get();
        const newList = llmInstances.filter(inst => inst.id !== id);
        set({ llmInstances: newList });
        persistConfig();
    },

    reorderLLMInstances: (instances: LLMInstance[]) => {
        set({ llmInstances: instances });
        persistConfig();
    },

    addOCRInstance: (instance: OCRInstance) => {
        const { ocrInstances } = get();
        const newList = [...ocrInstances, instance];
        set({ ocrInstances: newList });
        persistConfig();
    },

    updateOCRInstance: (id: string, updates: Partial<OCRInstance>) => {
        const { ocrInstances } = get();
        const newList = ocrInstances.map(inst => inst.id === id ? { ...inst, ...updates } : inst);
        set({ ocrInstances: newList });
        persistConfig();
    },

    deleteOCRInstance: (id: string) => {
        const { ocrInstances } = get();
        const newList = ocrInstances.filter(inst => inst.id !== id);
        set({ ocrInstances: newList });
        persistConfig();
    },

    reorderOCRInstances: (instances: OCRInstance[]) => {
        set({ ocrInstances: instances });
        persistConfig();
    },

    incrementOCRUsage: (id: string) => {
        const { ocrInstances } = get();
        const newList = ocrInstances.map(inst =>
            inst.id === id ? { ...inst, usedCount: inst.usedCount + 1 } : inst
        );
        set({ ocrInstances: newList });
        persistConfig();
    },

    updateOCRSettings: (settings: Partial<OCRSettings>) => {
        const { ocrSettings } = get();
        const newSettings = { ...ocrSettings, ...settings };
        set({ ocrSettings: newSettings });
        persistConfig();
    },

    addImageEditInstance: (instance: ImageEditInstance) => {
        const { imageEditInstances } = get();
        const newList = [...imageEditInstances, instance];
        set({ imageEditInstances: newList });
        persistConfig();
    },

    updateImageEditInstance: (id: string, updates: Partial<ImageEditInstance>) => {
        const { imageEditInstances } = get();
        const newList = imageEditInstances.map(inst => inst.id === id ? { ...inst, ...updates } : inst);
        set({ imageEditInstances: newList });
        persistConfig();
    },

    deleteImageEditInstance: (id: string) => {
        const { imageEditInstances } = get();
        const newList = imageEditInstances.filter(inst => inst.id !== id);
        set({ imageEditInstances: newList });
        persistConfig();
    },

    reorderImageEditInstances: (instances: ImageEditInstance[]) => {
        set({ imageEditInstances: instances });
        persistConfig();
    },

    updateImageEditSettings: (settings: Partial<ImageEditSettings>) => {
        const { imageEditSettings } = get();
        const newSettings = { ...imageEditSettings, ...settings };
        set({ imageEditSettings: newSettings });
        persistConfig();
    },

    updateTitleConfig: (updates: Partial<TitleOptimizationConfig>) => {
        const { titleConfig } = get();
        const newConfig = { ...titleConfig, ...updates };
        set({ titleConfig: newConfig });
        persistConfig();
    },

    updateLowQualityConfig: (updates: Partial<LowQualityFilterConfig>) => {
        const { lowQualityConfig } = get();
        const newConfig = { ...lowQualityConfig, ...updates };
        set({ lowQualityConfig: newConfig });
        persistConfig();
    },

    updateSizeChartConfig: (updates: Partial<SizeChartConfig>) => {
        const { sizeChartConfig } = get();
        const newConfig = { ...sizeChartConfig, ...updates };
        set({ sizeChartConfig: newConfig });
        persistConfig();
    },

    updateSkuConfig: (updates: Partial<SkuConfig>) => {
        const { skuConfig } = get();
        const newConfig = { ...skuConfig, ...updates };
        set({ skuConfig: newConfig });
        persistConfig();
    },

    toggleFieldVisibility: (fieldKey: string) => {
        const { hiddenFields } = get();
        let newFields: string[];
        if (hiddenFields.includes(fieldKey)) {
            newFields = hiddenFields.filter(k => k !== fieldKey);
        } else {
            newFields = [...hiddenFields, fieldKey];
        }
        set({ hiddenFields: newFields });
        persistConfig();
    },

    setAllFieldsVisible: () => {
        set({ hiddenFields: [] });
        persistConfig();
    },

    setFieldOrder: (order: string[]) => {
        set({ fieldOrder: order });
        persistConfig();
    },

    resetFieldOrder: () => {
        set({ fieldOrder: [] });
        persistConfig();
    },

    toggleStepConfirm: () => {
        const newVal = !get().stepConfirmEnabled;
        set({ stepConfirmEnabled: newVal });
        persistConfig();
    },

    setStepBatchSize: (size: number) => {
        const clamped = Math.max(1, Math.round(size));
        set({ stepBatchSize: clamped });
        persistConfig();
    },
}));
